import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { API_BASE_URL } from '../utils/api'
import LoadingSpinner from '../components/LoadingSpinner'
import { PlusIcon, EditIcon, TrashIcon } from '../components/Icons'

interface DealType {
  id: number
  name: string
  name_fr?: string
  supports_commercial: boolean
  supports_residential: boolean
  position: number
  is_active: boolean
  created_at: string
}

const DealTypesSettings: React.FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [loading, setLoading] = useState(true)
  const [dealTypes, setDealTypes] = useState<DealType[]>([])
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null)
  
  // Modal states
  const [showAddDealType, setShowAddDealType] = useState(false)
  const [editingDealType, setEditingDealType] = useState<DealType | null>(null)
  
  // New deal type state
  const [newDealType, setNewDealType] = useState({
    name: '',
    name_fr: ''
  })

  useEffect(() => {
    fetchDealTypes()
  }, [])

  const showMessage = (type: 'success' | 'error', text: string) => {
    setMessage({ type, text })
    setTimeout(() => setMessage(null), 5000)
  }

  const fetchDealTypes = async () => {
    try {
      const token = localStorage.getItem('token')
      if (!token) {
        showMessage('error', 'No authentication token')
        setLoading(false)
        return
      }

      const response = await fetch(`${API_BASE_URL}/api/settings/deal-types`, {
        headers: { 'Authorization': `Bearer ${token}` }
      })

      if (response.ok) {
        const data = await response.json()
        setDealTypes(data.deal_types || [])
      } else {
        console.error('Failed to fetch deal types')
        showMessage('error', 'Failed to load deal types')
      }
    } catch (error) {
      console.error('Error fetching deal types:', error)
      showMessage('error', 'Failed to load deal types')
    } finally {
      setLoading(false)
    }
  }

  const handleAddDealType = async () => {
    if (!newDealType.name.trim()) {
      showMessage('error', 'Deal type name is required')
      return
    }

    try {
      const token = localStorage.getItem('token')
      const response = await fetch(`${API_BASE_URL}/api/settings/deal-types`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: newDealType.name.trim(),
          name_fr: newDealType.name_fr.trim() || null,
          position: dealTypes.length + 1
        })
      })

      if (response.ok) {
        showMessage('success', 'Deal type added successfully!')
        setNewDealType({ name: '', name_fr: '' })
        setShowAddDealType(false)
        await fetchDealTypes()
      } else {
        const data = await response.json()
        showMessage('error', data.message || 'Failed to add deal type')
      }
    } catch (error) {
      console.error('Error adding deal type:', error)
      showMessage('error', 'Failed to add deal type')
    }
  }

  const handleEditDealType = async () => {
    if (!editingDealType || !editingDealType.name.trim()) {
      showMessage('error', 'Deal type name is required')
      return
    }

    try {
      const token = localStorage.getItem('token')
      const response = await fetch(`${API_BASE_URL}/api/settings/deal-types/${editingDealType.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: editingDealType.name.trim(),
          name_fr: editingDealType.name_fr?.trim() || null,
          is_active: editingDealType.is_active
        })
      })

      if (response.ok) {
        showMessage('success', 'Deal type updated successfully!')
        setEditingDealType(null)
        await fetchDealTypes()
      } else {
        const data = await response.json()
        showMessage('error', data.message || 'Failed to update deal type')
      }
    } catch (error) {
      console.error('Error updating deal type:', error)
      showMessage('error', 'Failed to update deal type')
    }
  }

  const handleDeleteDealType = async (typeId: number) => {
    if (!confirm('Are you sure you want to delete this deal type? This action cannot be undone.')) {
      return
    }

    try {
      const token = localStorage.getItem('token')
      const response = await fetch(`${API_BASE_URL}/api/settings/deal-types/${typeId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      })

      if (response.ok) {
        showMessage('success', 'Deal type deleted successfully!')
        await fetchDealTypes()
      } else {
        const data = await response.json()
        showMessage('error', data.message || 'Failed to delete deal type')
      }
    } catch (error) {
      console.error('Error deleting deal type:', error)
      showMessage('error', 'Failed to delete deal type')
    }
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <LoadingSpinner />
      </div>
    )
  }

  return (
    <div className="max-w-4xl mx-auto p-6">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-3">
          {t('dealTypesSettings.title')}
        </h1>
        <p className="text-gray-600 dark:text-gray-400">
          {t('dealTypesSettings.subtitle')}
        </p>
      </div>

      {/* Message Display */}
      {message && (
        <div className={`mb-6 p-4 rounded-md ${
          message.type === 'success' 
            ? 'bg-green-50 border border-green-200 text-green-800 dark:bg-green-900/20 dark:border-green-800 dark:text-green-200' 
            : 'bg-red-50 border border-red-200 text-red-800 dark:bg-red-900/20 dark:border-red-800 dark:text-red-200'
        }`}>
          {message.text}
        </div>
      )}

      {/* Add Deal Type Button */}
      <div className="mb-6">
        <button
          onClick={() => setShowAddDealType(true)}
          className="flex items-center px-4 py-2 bg-gray-900 text-white rounded-md hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-gray-900 transition-colors"
        >
          <PlusIcon className="mr-2" size={20} />
          {t('dealTypesSettings.addDealType')}
        </button>
      </div>

      {/* Deal Types List */}
      <div className="bg-white dark:bg-gray-800 shadow rounded-lg border border-gray-200 dark:border-gray-700">
        <div className="p-6">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-6">
            {t('dealTypesSettings.availableDealTypes')}
          </h2>

          <div className="space-y-4">
            {dealTypes.map((dealType) => (
              <div
                key={dealType.id}
                className="flex items-center justify-between p-4 border border-gray-200 dark:border-gray-600 rounded-lg hover:shadow-sm transition-shadow"
              >
                <div className="flex items-center space-x-4">
                  <div className={`w-3 h-3 rounded-full ${dealType.is_active ? 'bg-green-500' : 'bg-gray-400'}`} />
                  <div>
                    <h3 className="font-medium text-gray-900 dark:text-white">
                      {dealType.name}
                    </h3>
                    {dealType.name_fr && (
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        French: {dealType.name_fr}
                      </p>
                    )}
                    <div className="flex items-center space-x-2 mt-1">
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {dealType.is_active ? t('dealTypesSettings.active') : t('dealTypesSettings.inactive')}
                      </p>
                      {(dealType.supports_commercial || dealType.supports_residential) && (
                        <div className="flex items-center space-x-1">
                          {dealType.supports_commercial && (
                            <span className="px-2 py-1 bg-blue-100 dark:bg-blue-900/20 text-blue-800 dark:text-blue-200 text-xs rounded">
                              {t('dealTypesSettings.commercial')}
                            </span>
                          )}
                          {dealType.supports_residential && (
                            <span className="px-2 py-1 bg-green-100 dark:bg-green-900/20 text-green-800 dark:text-green-200 text-xs rounded">
                              {t('dealTypesSettings.residential')}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => setEditingDealType(dealType)}
                    className="p-2 text-gray-400 hover:text-gray-900 dark:hover:text-gray-600 transition-colors"
                  >
                    <EditIcon size={16} />
                  </button>
                  <button
                    onClick={() => handleDeleteDealType(dealType.id)}
                    className="p-2 text-gray-400 hover:text-red-600 dark:hover:text-red-400 transition-colors"
                  >
                    <TrashIcon size={16} />
                  </button>
                </div>
              </div>
            ))}
          </div>

          {dealTypes.length === 0 && (
            <div className="text-center py-8">
              <p className="text-gray-500 dark:text-gray-400 mb-4">{t('dealTypesSettings.noDealTypesConfigured')}</p>
              <button
                onClick={() => setShowAddDealType(true)}
                className="px-4 py-2 bg-gray-900 text-white rounded-md hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-gray-900 transition-colors"
              >
                {t('dealTypesSettings.createFirstDealType')}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Add Deal Type Modal */}
      {showAddDealType && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              {t('dealTypesSettings.addDealType')}
            </h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('dealTypesSettings.dealTypeName')}
                </label>
                <input
                  type="text"
                  value={newDealType.name}
                  onChange={(e) => setNewDealType({...newDealType, name: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-gray-900"
                  placeholder={t('dealTypesSettings.dealTypeNamePlaceholder')}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('dealTypesSettings.frenchName')}
                </label>
                <input
                  type="text"
                  value={newDealType.name_fr}
                  onChange={(e) => setNewDealType({...newDealType, name_fr: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-gray-900"
                  placeholder={t('dealTypesSettings.frenchNamePlaceholder')}
                />
              </div>
            </div>

            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => {
                  setShowAddDealType(false)
                  setNewDealType({ name: '', name_fr: '' })
                }}
                className="px-4 py-2 text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 dark:bg-gray-600 dark:text-gray-300 dark:hover:bg-gray-500 transition-colors"
              >
                {t('dealTypesSettings.cancel')}
              </button>
              <button
                onClick={handleAddDealType}
                className="px-4 py-2 bg-gray-900 text-white rounded-md hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-gray-900 transition-colors"
              >
                {t('dealTypesSettings.addDealType')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Deal Type Modal */}
      {editingDealType && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Edit Deal Type
            </h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  English Name
                </label>
                <input
                  type="text"
                  value={editingDealType.name}
                  onChange={(e) => setEditingDealType({...editingDealType, name: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-gray-900"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  French Name (Optional)
                </label>
                <input
                  type="text"
                  value={editingDealType.name_fr || ''}
                  onChange={(e) => setEditingDealType({...editingDealType, name_fr: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-gray-900"
                />
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="is_active"
                  checked={editingDealType.is_active}
                  onChange={(e) => setEditingDealType({...editingDealType, is_active: e.target.checked})}
                  className="w-4 h-4 text-gray-900 bg-gray-100 border-gray-300 rounded focus:ring-gray-900 dark:focus:ring-gray-900 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                />
                <label htmlFor="is_active" className="ml-2 text-sm text-gray-900 dark:text-gray-300">
                  Active
                </label>
              </div>
            </div>

            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => setEditingDealType(null)}
                className="px-4 py-2 text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 dark:bg-gray-600 dark:text-gray-300 dark:hover:bg-gray-500 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleEditDealType}
                className="px-4 py-2 bg-gray-900 text-white rounded-md hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-gray-900 transition-colors"
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default DealTypesSettings 